import type { MindsetVitalClient } from './mindset-types'

/**
 * Backpressure for Mindset's frame pump, applied from OUTSIDE the vendor bundle (KAN-3; D-38, D-42, D-62).
 *
 * **What the vendor does with every frame.** `vital-client.js` (readable in the sourcemap beside the
 * bundle) answers EVERY `requestVideoFrameCallback` with a `createImageBitmap` of the whole frame and a
 * one-way notification to its worker. On iOS that notification sends the bitmap WITHOUT transferring it
 * (`worker-manager.js`: `isIOS ? notifiers.detectVitals(frameInfo, imgBitmap)`), so each frame is
 * structured-cloned - 1.2 MB at 640x480 - and nothing waits for the worker to finish the last one. When
 * the worker is slower than the camera, the clones queue in its inbox without limit.
 *
 * That is the likeliest reading of D-38's "the tab was killed about nineteen seconds into the
 * measurement": a worker at ~25 fps behind a 60 fps camera falls ~35 frames a second behind, ~40 MB/s,
 * which crosses WebKit's ~1.5 GB per-tab ceiling (D-54's JetsamEvent) in about twenty seconds - and in a
 * concurrent hybrid, Shen.AI's own threads take CPU from that worker, so it falls behind faster. Two
 * WASM heaps are tens of megabytes; an unbounded frame queue is not. (Shen.AI does not have this
 * problem: its iOS path paces itself at 30 fps and drops a frame when too many bytes are in flight.)
 *
 * **What this does.** The worker announces every frame it finishes as a `signsMessage`. So the gate
 * counts frames handed to the vendor's callback against frames the worker has answered, and while
 * `maxInFlight` are outstanding it lets the browser's frame go by unseen instead of handing it to the
 * vendor. Mindset therefore receives frames at the rate its worker can process them - which is all the
 * frames it would ever have processed in time anyway - and the queue is bounded at a few frames instead
 * of growing for the length of the scan. The scan is exactly as long as before.
 *
 * A frame the worker skips without answering (it has three `return 0` paths that send no signs) would
 * otherwise hold a slot forever, so a slot is also released after `staleAfterMs`.
 *
 * It patches the ONE video element the vendor is given, as own properties that shadow the prototype's,
 * and `release()` deletes them again. `cancelVideoFrameCallback` is patched with it, because the handle
 * the vendor holds is no longer the browser's: a gate that re-requests a frame gets a new browser handle
 * each time, and the vendor's `stop()` cancels by the handle it was given.
 */
export interface MindsetFrameGate {
  /** Counts since the gate was installed, for the log and the diagnostics overlay. */
  stats(): MindsetFrameGateStats
  /** Puts the element's own methods back. Idempotent. */
  release(): void
}

export interface MindsetFrameGateStats {
  readonly handedToVendor: number
  readonly answeredByWorker: number
  readonly heldBack: number
  readonly inFlight: number
  readonly releasedAsStale: number
}

export interface MindsetFrameGateOptions {
  /** How many frames may be with the worker at once. Two keeps one in hand while the other is processed. */
  readonly maxInFlight?: number
  /** After this long an unanswered frame stops counting against the limit. */
  readonly staleAfterMs?: number
}

type FrameCallback = (now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => void

export function gateMindsetFrames(
  video: HTMLVideoElement,
  client: MindsetVitalClient,
  options: MindsetFrameGateOptions = {},
): MindsetFrameGate {
  const maxInFlight = options.maxInFlight ?? 2
  const staleAfterMs = options.staleAfterMs ?? 1_000

  if (typeof video.requestVideoFrameCallback !== 'function') {
    // The vendor cannot pump frames on this browser at all; nothing to gate.
    return { stats: () => emptyStats(), release: () => {} }
  }

  const nativeRequest = video.requestVideoFrameCallback.bind(video)
  const nativeCancel = video.cancelVideoFrameCallback.bind(video)

  /** Send times of frames handed to the vendor and not yet answered, oldest first. */
  const outstanding: number[] = []
  let handedToVendor = 0
  let answeredByWorker = 0
  let heldBack = 0
  let releasedAsStale = 0
  let released = false

  /** The vendor's handle -> the browser handle currently standing in for it. */
  const handles = new Map<number, number>()
  let nextHandle = 1

  const dropStale = (now: number) => {
    while (outstanding.length > 0 && now - outstanding[0] > staleAfterMs) {
      outstanding.shift()
      releasedAsStale++
    }
  }

  client.on('signsMessage', () => {
    answeredByWorker++
    outstanding.shift()
  })

  const gatedRequest = (callback: FrameCallback): number => {
    if (released) return nativeRequest(callback)

    const handle = nextHandle++
    const gate: FrameCallback = (now, metadata) => {
      if (!handles.has(handle)) return
      const at = performance.now()
      dropStale(at)
      if (outstanding.length >= maxInFlight) {
        heldBack++
        handles.set(handle, nativeRequest(gate))
        return
      }
      handles.delete(handle)
      outstanding.push(at)
      handedToVendor++
      callback(now, metadata)
    }
    handles.set(handle, nativeRequest(gate))
    return handle
  }

  const gatedCancel = (handle: number): void => {
    const current = handles.get(handle)
    if (current !== undefined) {
      handles.delete(handle)
      nativeCancel(current)
      return
    }
    // Not one of ours - a handle issued before the gate, or after release.
    nativeCancel(handle)
  }

  Object.defineProperty(video, 'requestVideoFrameCallback', { value: gatedRequest, configurable: true, writable: true })
  Object.defineProperty(video, 'cancelVideoFrameCallback', { value: gatedCancel, configurable: true, writable: true })

  return {
    stats: () => ({
      handedToVendor,
      answeredByWorker,
      heldBack,
      inFlight: outstanding.length,
      releasedAsStale,
    }),
    release: () => {
      if (released) return
      released = true
      for (const current of handles.values()) nativeCancel(current)
      handles.clear()
      delete (video as Partial<HTMLVideoElement>).requestVideoFrameCallback
      delete (video as Partial<HTMLVideoElement>).cancelVideoFrameCallback
    },
  }
}

function emptyStats(): MindsetFrameGateStats {
  return { handedToVendor: 0, answeredByWorker: 0, heldBack: 0, inFlight: 0, releasedAsStale: 0 }
}
